import { TranscarentSsoControllerService } from "@9amhealth/openapi";
import React, { FC, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FunnelKey from "src/constants/funnelKey";
import envVariables from "src/lib/envVariables";
import parseJwt from "src/lib/parseJwt";
import { SAML_FUNNEL_KEY_SESSION_STORAGE_KEY } from "src/lib/useSamlAuth";
import { appViewState, authenticationState } from "src/state/state";
import { NiceError } from "src/ui/styled/NiceError";
import Loader from "../Loader/Loader";
import Translate from "../Translate/Translate";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { handleAuthSuccess } from "./handleAuthSuccess";

const HandleOneTimeToken: FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<unknown | null>(null);
  const token = searchParams.get("token") ?? searchParams.get("lookupKey");
  const funnel = FunnelKey.transcarent;
  const isProd = envVariables.APP_ENV === "production";
  const authStage = useUserAuthStage();
  const navigate = useNavigate();
  const [finishedHandover, setFinishedHandover] = React.useState(false);
  appViewState.setPartnerSession(true);

  const exchangeTokenDetails = useMemo(() => {
    if (token) {
      try {
        const decoded = parseJwt(token);
        return {
          token,
          userId: decoded.sub
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        setError("Invalid Exchange Token");
        return null;
      }
    }
    setError("Missing Exchange Token");
    return null;
  }, [token]);

  useEffect(() => {
    if (exchangeTokenDetails) {
      // Prepare app for external exchange to avoid stale sessions
      authenticationState.prepareForExternalTokenLogin();
      sessionStorage.setItem(SAML_FUNNEL_KEY_SESSION_STORAGE_KEY, funnel);

      void TranscarentSsoControllerService.exchangeToken({
        token: exchangeTokenDetails.token
      })
        .then((res) => {
          authenticationState.externalLoginInProgress = false;

          void authenticationState
            .setCredentials({
              accessToken: res.data.accessToken,
              refreshToken: res.data.refreshToken
            })
            .then(() => {
              setFinishedHandover(true);
              handleAuthSuccess(navigate);
            })
            .catch(() => {
              setFinishedHandover(true);
              setError("Could not set credentials");
            });
        })
        .catch((err: unknown) => {
          authenticationState.externalLoginInProgress = false;
          setResponse(err);
          if (err instanceof Error) {
            setError(err.message);
          }
        });
    }
  }, [exchangeTokenDetails]);

  useEffect(() => {
    if (error) {
      reportErrorSentry("HandleOneTimeToken error", {
        error,
        response
      });
    }
    if (
      (authStage === "noAuth" || authStage === "nullAuth") &&
      finishedHandover
    ) {
      // Log failed login attempt
      reportErrorSentry("HandleOneTimeToken login failed", {
        authStage,
        finishedHandover
      });
    }
  }, [error, authStage, finishedHandover, response]);

  if (error) {
    return (
      <NiceError>
        <h1>
          <Translate msg="auth.ott.error" />
        </h1>
        {!isProd && (
          <>
            <em>error:</em>
            <pre>{error}</pre>
            <em>response:</em>
            <pre>{JSON.stringify(response, null, 2)}</pre>
          </>
        )}
      </NiceError>
    );
  }

  if (
    (authStage === "noAuth" || authStage === "nullAuth") &&
    finishedHandover
  ) {
    return (
      <NiceError>
        <h1>
          <Translate msg="auth.loginFailed" />
        </h1>
      </NiceError>
    );
  }

  return <Loader active fixed absolute fullPage bg="partner" />;
};

export default HandleOneTimeToken;
